import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Colori IDENTICI a pesdb.net: stesso verde, principale (pos2) PIENO, secondario (pos1) al 50%.
const COLOR_PRIMARY = "rgba(50, 200, 90, 1)";
const COLOR_SECONDARY = "rgba(50, 200, 90, 0.5)";
const STROKE = "rgb(var(--tc-2bb6ff))";

/**
 * Mappa ruoli replicata 1:1 da pesdb.net.
 * Il campo pesdb è esattamente 120×153 px e ogni ruolo è un rettangolo a coordinate fisse
 * (estratte dal CSS ufficiale di pesdb: `.pitch > .<code> { left/top/width/height }`).
 * Attacco in alto, porta in basso.
 */
const ZONES = {
    cf:  { x: 32,  y: 2,   w: 56, h: 21 },
    ss:  { x: 30,  y: 26,  w: 60, h: 21 },
    lwf: { x: 2,   y: 2,   w: 27, h: 45 },
    rwf: { x: 91,  y: 2,   w: 27, h: 45 },
    amf: { x: 30,  y: 48,  w: 60, h: 18 },
    lmf: { x: 2,   y: 48,  w: 27, h: 57 },
    rmf: { x: 91,  y: 48,  w: 27, h: 57 },
    cmf: { x: 30,  y: 67,  w: 60, h: 19 },
    dmf: { x: 30,  y: 87,  w: 60, h: 18 },
    lb:  { x: 2,   y: 106, w: 27, h: 45 },
    rb:  { x: 91,  y: 106, w: 27, h: 45 },
    cb:  { x: 30,  y: 106, w: 60, h: 21 },
    gk:  { x: 32,  y: 130, w: 56, h: 21 },
};

const ALL_CODES = Object.keys(ZONES);

const LABELS = {
    gk: "GK", cb: "CB", lb: "LB", rb: "RB",
    dmf: "DMF", cmf: "CMF", lmf: "LMF", rmf: "RMF",
    amf: "AMF", lwf: "LWF", rwf: "RWF", ss: "SS", cf: "CF",
};

export default function PositionField({ pesId, compact = false }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!pesId) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/players/${pesId}/positions`);
                if (!cancelled) setData(data);
            } catch {
                if (!cancelled) setData(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [pesId]);

    if (loading) {
        return (
            <div className={compact ? "" : "op-card p-4"} data-testid="position-field-loading">
                <div className="op-label text-t8f95a3 animate-pulse text-[10px]">// MAPPA RUOLI…</div>
            </div>
        );
    }

    const primary = new Set((data?.primary || []).map((s) => s.toLowerCase()));
    const secondary = new Set((data?.secondary || []).map((s) => s.toLowerCase()));
    const hasAny = primary.size > 0 || secondary.size > 0;

    if (compact) {
        return (
            <div
                className="border border-t22252a p-3 flex flex-col items-stretch h-full"
                data-testid="position-field"
            >
                <div className="op-label text-[10px] mb-2" style={{ color: COLOR_PRIMARY }}>
                    // MAPPA RUOLI
                </div>
                {!hasAny ? (
                    <div className="op-mono text-[10px] text-t8f95a3 italic flex-1 flex items-center justify-center" data-testid="position-field-empty">
                        N/D
                    </div>
                ) : (
                    <div className="flex items-center gap-3 flex-1">
                        <svg viewBox="0 0 120 153" className="h-[140px] w-auto flex-shrink-0" style={{ background: "rgb(var(--tc-0a3d1a))", border: `1px solid ${STROKE}` }}>
                            <PositionRects primary={primary} secondary={secondary} />
                            <PitchLines />
                        </svg>
                        <div className="flex flex-col gap-1 flex-1 min-w-0">
                            <div className="op-mono text-[9px] text-t8f95a3 flex items-center gap-1.5">
                                <span className="inline-block w-2 h-2" style={{ background: COLOR_PRIMARY, boxShadow: `0 0 4px ${COLOR_PRIMARY}` }} />
                                Pref.
                            </div>
                            <div className="op-mono text-[10px] text-tffffff leading-tight break-words">
                                {Array.from(primary).map((c) => LABELS[c]).join(" · ") || "—"}
                            </div>
                            <div className="op-mono text-[9px] text-t8f95a3 flex items-center gap-1.5 mt-1">
                                <span className="inline-block w-2 h-2" style={{ background: COLOR_SECONDARY }} />
                                Sec.
                            </div>
                            <div className="op-mono text-[10px] text-tc9ccd3 leading-tight break-words">
                                {Array.from(secondary).map((c) => LABELS[c]).join(" · ") || "—"}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    return (
        <div className="op-card p-4" data-testid="position-field">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="op-label text-[10px]" style={{ color: COLOR_PRIMARY }}>
                    // MAPPA RUOLI
                </div>
                <div className="flex items-center gap-3 op-mono text-[9px] text-t8f95a3">
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5" style={{ background: COLOR_PRIMARY, boxShadow: `0 0 6px ${COLOR_PRIMARY}` }} />
                        Preferita
                    </span>
                    <span className="flex items-center gap-1.5">
                        <span className="inline-block w-2.5 h-2.5" style={{ background: COLOR_SECONDARY }} />
                        Secondaria
                    </span>
                </div>
            </div>

            {!hasAny && (
                <div className="op-mono text-[11px] text-t8f95a3 italic mb-2" data-testid="position-field-empty">
                    Dati ruoli non disponibili.
                </div>
            )}

            <div className="w-full mx-auto" style={{ maxWidth: "300px" }}>
                <svg viewBox="0 0 120 153" className="w-full h-auto" style={{ background: "rgb(var(--tc-0a3d1a))", border: `1px solid ${STROKE}` }}>
                    <PositionRects primary={primary} secondary={secondary} />
                    <PitchLines />
                </svg>
            </div>
        </div>
    );
}

function PositionRects({ primary, secondary }) {
    return (
        <>
            {ALL_CODES.map((code) => {
                const z = ZONES[code];
                const isP = primary.has(code);
                const isS = !isP && secondary.has(code);
                if (!isP && !isS) return null;
                const fill = isP ? COLOR_PRIMARY : COLOR_SECONDARY;
                return (
                    <rect
                        key={code}
                        x={z.x}
                        y={z.y}
                        width={z.w}
                        height={z.h}
                        fill={fill}
                        stroke="none"
                        data-testid={`pos-${code}${isP ? "-primary" : "-secondary"}`}
                    >
                        <title>{LABELS[code]}{isP ? " — Preferita" : " — Secondaria"}</title>
                    </rect>
                );
            })}
        </>
    );
}

function PitchLines() {
    return (
        <g fill="none" stroke={STROKE} strokeWidth="0.7" opacity="0.85">
            <rect x="2" y="2" width="116" height="149" />
            <line x1="2" y1="76.5" x2="118" y2="76.5" />
            <circle cx="60" cy="76.5" r="11" />
            {/* Area di rigore in alto (attacco) */}
            <rect x="28" y="2" width="64" height="18" />
            <rect x="44" y="2" width="32" height="6" />
            <path d="M 49 20 A 11 11 0 0 0 71 20" />
            {/* Area di rigore in basso (porta) */}
            <rect x="28" y="133" width="64" height="18" />
            <rect x="44" y="145" width="32" height="6" />
            <path d="M 49 133 A 11 11 0 0 1 71 133" />
        </g>
    );
}
